import {logout} from '../Services/auth';
import {AxiosRequestConfig} from 'axios';
import {MainRequest} from '../Services/common';
import Config from '../config';
import {QueryClient} from 'react-query';
import {message} from 'antd';
import dayjs from 'dayjs';

// 判断是否布尔真
export function isTrue(value: BooleanValue): value is BooleanTrue {
    const TrueValueArray: any[] = [1, '1', true, 'true', 'True', 'TRUE'];
    return TrueValueArray.includes(value);
}

/**
 * 取得对象的第一个值
 * @param obj
 */
export function objectGetFirstValue(obj: Object) {
    const keys = Object.keys(obj);
    if (keys.length === 0) return null;
    return obj[keys[0] as keyof typeof obj];
}

/**
 * 从错误对象中获取错误消息
 * @param e
 * @param defaultMessage
 */
export function errorMessage(
    e: any,
    defaultMessage: string = '数据请求失败',
): string {
    if (e?.response?.data?.data?.message) {
        return e?.response?.data?.data?.message;
    }

    return defaultMessage;
}

// 解析JPlus的错误消息
export function getJPlusError(message: any): string {
    if (typeof message !== 'string') return '';
    if (message.indexOf('{success=false') !== 0) return '';
    return message.match(/errorMessage=[\w.]+:(.*),/)?.[1] || '';
}

// 将各种类型的值转成数值类型
export function toNumber(value: number | string | null | undefined) {
    if (!value) return 0;
    return Number(value) || 0;
}

/**
 * 去登录
 */
export async function goLogin() {
    await logout();
    window.location.href = '/login';
}

/**
 * 生成一个字典对象
 * @param data
 */
export function makeDict<T extends DictType>(data: T[]) {
    const firstValue = data[0]?.value;

    function getName(key?: string | BooleanValue) {
        return data.find(item => String(item.value) === String(key))?.label;
    }

    function getCurrent<V>(key: string) {
        return data.find(item => String(item.value) === String(key)) as V & T;
    }

    return {data, getName, firstValue, getCurrent};
}

export type DictInstance = ReturnType<typeof makeDict>;

// 从disposition中获取文件名
export function getFileNameFromDisposition(disposition: string): string {
    if (!disposition) return '';
    const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-.]+)(?:; ?|$)/i;
    const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;

    let fileName: string = '';
    if (utf8FilenameRegex.test(disposition)) {
        fileName = decodeURIComponent(
            utf8FilenameRegex.exec(disposition)?.[1] || '',
        );
    } else {
        // prevent ReDos attacks by anchoring the ascii regex to string start and
        //  slicing off everything before 'filename='
        const filenameStart = disposition.toLowerCase().indexOf('filename=');
        if (filenameStart >= 0) {
            const partialDisposition = disposition.slice(filenameStart);
            const matches = asciiFilenameRegex.exec(partialDisposition);
            if (matches != null && matches[2]) {
                fileName = decodeURIComponent(matches[2]);
            }
        }
    }
    return fileName;
}

/**
 * 下载文件
 * @param url
 * @param config
 */
export function download(url: string, config?: AxiosRequestConfig) {
    return MainRequest({
        url,
        method: 'GET',
        responseType: 'blob', // important
        ...config,
    });
}

/**
 * 获取列表项序号
 * @param index
 * @param pagination
 */
export function getPagedIndex(index: number, pagination: Pagination) {
    return index + 1 + (pagination.pageNo - 1) * pagination.pageSize;
}

/**
 * 弹出容器
 * @param trigger
 */
export function getPopupContainer(trigger: any) {
    return trigger.parentNode;
}

/**
 * 浏览器里过滤下拉列表的节点
 * @param inputValue
 * @param option
 */
export function filterOption(inputValue: string, option?: any) {
    return (
        option?.label
            ?.toString()
            .toLowerCase()
            .indexOf(String(inputValue).toLowerCase()) !== -1
    );
}

/**
 * 文件尺寸格式化
 * @param bytes
 * @param dp
 */
export function humanFileSize(bytes: number, dp = 0) {
    const thresh = 1024;

    if (Math.abs(bytes) < thresh) {
        return bytes + ' B';
    }

    const units = ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let u = -1;
    const r = 10 ** dp;

    do {
        bytes /= thresh;
        ++u;
    } while (
        Math.round(Math.abs(bytes) * r) / r >= thresh &&
        u < units.length - 1
    );

    return bytes.toFixed(dp) + units[u];
}

/**
 * 判断是否未chrome浏览器
 */
export function isChrome() {
    return (
        /Chrome/.test(navigator.userAgent) &&
        /Google Inc/.test(navigator.vendor)
    );
}

export function getDownloadFileName(key: string, fileName: string) {
    return `${Config.QiNiuDownloadUrl}/${key}?attname=${fileName}`;
}

/**
 * 对树形进行reduce
 * @param tree
 * @param reducer
 * @param memo
 */
export function reduceTree<T extends TreeNode<unknown, 'children'>, R>(
    tree: T[],
    reducer: (value: T, memo: R) => R,
    memo: R,
) {
    if (!tree) return memo;

    for (const node of tree) {
        memo = reducer(node, memo);
        memo = reduceTree(node.children as any, reducer, memo);
    }

    return memo;
}

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            onError: (e: any) => {
                message.error(errorMessage(e));
            },
        },
        mutations: {
            onError: (e: any) => {
                message.error(errorMessage(e));
            },
        },
    },
});

export function traverseTree(node: any, visit: any) {
    if (node === null) {
        return;
    }
    visit(node);
    if (node?.children) {
        node?.children?.forEach(function (child: any) {
            traverseTree(child, visit);
        });
    }
}
export function removeSpaces(obj: any) {
    for (var key in obj) {
        if (typeof obj[key] === 'string') {
            obj[key] = obj[key].replace(/ /g, ''); // 将字符串中的空格全部替换为空
            obj[key] = obj[key].replace('\t', ''); // 将字符串中的空格全部替换为空
        } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
            removeSpaces(obj[key]); // 递归调用函数处理子对象
        }
    }
}

// 格式化日期
export function formatDate(date?: string, format: string = 'YYYY-MM-DD') {
    if (!date) return '';
    return dayjs(date).format(format);
}

export const errImg =
    'data:image/jpeg;base64,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';

/**
 * 休眠 time毫秒
 * time: 毫秒
 */
export function sleep(time: number) {
    return new Promise(resolve => setTimeout(resolve, time));
}

type HiddenListItem<T> = {
    hidden?: boolean; // 没有该属性的不会被过滤
} & T;

/**
 * 过滤掉hidden列表项
 * @param data
 */
export function filterHiddenListItem<T>(data: HiddenListItem<T>[]): T[] {
    return data.filter(item => !item.hidden);
}
